import { LinearScale, LogarithmicScale, } from 'chart.js';
export const baseDefaults = {
    position: 'chartArea',
    property: 'value',
    grid: {
        z: 1,
        drawOnChartArea: false,
    },
    ticks: {
        z: 1,
    },
    legend: {
        align: 'right',
        position: 'bottom-right',
        length: 100,
        width: 50,
        margin: 8,
        indicatorWidth: 10,
    },
};
function computeLegendMargin(legend) {
    const { indicatorWidth, align: pos, margin } = legend;
    const left = (typeof margin === 'number' ? margin : margin.left) + (pos === 'right' ? indicatorWidth : 0);
    const top = (typeof margin === 'number' ? margin : margin.top) + (pos === 'bottom' ? indicatorWidth : 0);
    const right = (typeof margin === 'number' ? margin : margin.right) + (pos === 'left' ? indicatorWidth : 0);
    const bottom = (typeof margin === 'number' ? margin : margin.bottom) + (pos === 'top' ? indicatorWidth : 0);
    return { left, top, right, bottom };
}
function computeLegendPosition(chartArea, legend, width, height, legendSize) {
    const { indicatorWidth, align: axisPos, position: pos } = legend;
    const isHor = axisPos === 'top' || axisPos === 'bottom';
    const w = (axisPos === 'left' ? legendSize.w : width) + (isHor ? indicatorWidth : 0);
    const h = (axisPos === 'top' ? legendSize.h : height) + (!isHor ? indicatorWidth : 0);
    const margin = computeLegendMargin(legend);
    if (typeof pos === 'string') {
        switch (pos) {
            case 'top-left':
                return [margin.left, margin.top];
            case 'top':
                return [(chartArea.right - w) / 2, margin.top];
            case 'left':
                return [margin.left, (chartArea.bottom - h) / 2];
            case 'top-right':
                return [chartArea.right - w - margin.right, margin.top];
            case 'bottom-right':
                return [chartArea.right - w - margin.right, chartArea.bottom - h - margin.bottom];
            case 'bottom':
                return [(chartArea.right - w) / 2, chartArea.bottom - h - margin.bottom];
            case 'bottom-left':
                return [margin.left, chartArea.bottom - h - margin.bottom];
            default:
                return [chartArea.right - w - margin.right, (chartArea.bottom - h) / 2];
        }
    }
    return [pos.x, pos.y];
}
export class LegendScale extends LinearScale {
    constructor() {
        super(...arguments);
        this.legendSize = { w: 0, h: 0 };
    }
    init(options) {
        options.position = 'chartArea';
        super.init(options);
        this.axis = 'r';
    }
    parse(raw, index) {
        if (raw && typeof raw[this.options.property] === 'number') {
            return raw[this.options.property];
        }
        return super.parse(raw, index);
    }
    isHorizontal() {
        return this.options.legend.align === 'top' || this.options.legend.align === 'bottom';
    }
    _getNormalizedValue(v) {
        if (v == null || Number.isNaN(v)) {
            return null;
        }
        return (v - this._startValue) / this._valueRange;
    }
    update(maxWidth, maxHeight, margins) {
        const ch = Math.min(maxHeight, this.bottom == null ? Number.POSITIVE_INFINITY : this.bottom);
        const cw = Math.min(maxWidth, this.right == null ? Number.POSITIVE_INFINITY : this.right);
        const l = this.options.legend;
        const isHor = this.isHorizontal();
        const factor = (v, full) => (v < 1 ? full * v : v);
        const w = Math.min(cw, factor(isHor ? l.length : l.width, cw)) - (!isHor ? l.indicatorWidth : 0);
        const h = Math.min(ch, factor(!isHor ? l.length : l.width, ch)) - (isHor ? l.indicatorWidth : 0);
        this.legendSize = { w, h };
        this.bottom = h;
        this.height = h;
        this.right = w;
        this.width = w;
        const bak = this.options.position;
        this.options.position = this.options.legend.align;
        const r = super.update(w, h, margins);
        this.options.position = bak;
        this.height = Math.min(h, this.height);
        this.width = Math.min(w, this.width);
        return r;
    }
    _computeLabelArea() {
        return undefined;
    }
    draw(chartArea) {
        if (!this._isVisible()) {
            return;
        }
        const pos = computeLegendPosition(chartArea, this.options.legend, this.width, this.height, this.legendSize);
        const { ctx } = this;
        ctx.save();
        ctx.translate(pos[0], pos[1]);
        const bak = this.options.position;
        this.options.position = this.options.legend.align;
        super.draw({ ...chartArea, bottom: this.height + 10, right: this.width });
        this.options.position = bak;
        const { indicatorWidth } = this.options.legend;
        switch (this.options.legend.align) {
            case 'left':
                ctx.translate(this.legendSize.w, 0);
                break;
            case 'top':
                ctx.translate(0, this.legendSize.h);
                break;
            case 'bottom':
                ctx.translate(0, -indicatorWidth);
                break;
            default:
                ctx.translate(-indicatorWidth, 0);
                break;
        }
        this._drawIndicator();
        ctx.restore();
    }
    _drawIndicator() {
    }
}
export class LogarithmicLegendScale extends LogarithmicScale {
    constructor() {
        super(...arguments);
        this.legendSize = { w: 0, h: 0 };
    }
    init(options) {
        LegendScale.prototype.init.call(this, options);
    }
    parse(raw, index) {
        return LegendScale.prototype.parse.call(this, raw, index);
    }
    isHorizontal() {
        return this.options.legend.align === 'top' || this.options.legend.align === 'bottom';
    }
    _getNormalizedValue(v) {
        if (v == null || Number.isNaN(v)) {
            return null;
        }
        return (Math.log10(v) - this._startValue) / this._valueRange;
    }
    update(maxWidth, maxHeight, margins) {
        return LegendScale.prototype.update.call(this, maxWidth, maxHeight, margins);
    }
    _computeLabelArea() {
        return undefined;
    }
    draw(chartArea) {
        return LegendScale.prototype.draw.call(this, chartArea);
    }
    _drawIndicator() {
    }
}
//# sourceMappingURL=LegendScale.js.map